import { useState, useRef, useEffect } from "react";
import { X, Send, Volume2, VolumeX } from "lucide-react";

const MAX_MESSAGE_LENGTH = 500;

const GREETING = {
  role: "assistant",
  content: "Hi, I'm Erick's AI — ask me about my work, coffee, photography, or anything else on this site.",
};

export default function ChatPanel({ onClose }) {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [muted, setMuted] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const playSpeech = async (text, signature) => {
    if (!signature) return;

    try {
      const res = await fetch("/api/speech", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, signature }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.play();
    } catch {
      // Voice is a bonus on top of the text reply — fail silently.
    }
  };

  const sendMessage = async () => {
    const trimmed = input.trim().slice(0, MAX_MESSAGE_LENGTH);
    if (!trimmed || isLoading) return;

    const nextMessages = [...messages, { role: "user", content: trimmed }];
    setMessages(nextMessages);
    setInput("");
    setIsLoading(true);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: nextMessages }),
      });
      const data = await res.json();

      if (!res.ok || !data.reply) {
        throw new Error(data.error || "Something went wrong.");
      }

      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      if (!muted) playSpeech(data.reply, data.signature);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Sorry, something went wrong: ${err.message}`, isError: true },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] max-w-sm h-[28rem] bg-card border border-border rounded-lg shadow-xl flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-sm font-medium text-foreground">Chat with Erick</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMuted((prev) => !prev)}
            className="text-muted hover:text-foreground transition-colors"
            aria-label={muted ? "Unmute voice" : "Mute voice"}
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <button onClick={onClose} className="text-muted hover:text-foreground transition-colors" aria-label="Close chat">
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                message.role === "user"
                  ? "bg-foreground text-background"
                  : message.isError
                  ? "bg-border/40 text-muted italic"
                  : "bg-border/40 text-foreground"
              }`}
            >
              {message.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-border/40 text-muted rounded-lg px-3 py-2 text-sm">...</div>
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      <div className="flex items-center gap-2 px-3 py-3 border-t border-border">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
          onKeyDown={handleKeyDown}
          placeholder="Ask me anything..."
          className="flex-1 text-sm bg-background border border-border text-foreground rounded px-3 py-2 focus:outline-none focus:border-foreground/40"
        />
        <button
          onClick={sendMessage}
          disabled={!input.trim() || isLoading}
          className="flex-shrink-0 w-9 h-9 rounded-full bg-foreground text-background flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
          aria-label="Send message"
        >
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}
